import React from 'react';
import { CreateRouteMapProps } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useGetOrderQuery } from '@src/features/order/redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { MissionIdParams } from '@src/core/entities';
import { useWizardOrderId } from '@src/features/order/pages/upsert-mission-page/hooks/useWizardOrderId';
import { selectDrone, selectEditableRoute, selectRouteModified, selectWaypoints, setWaypoints } from '@src/features/order/pages/upsert-mission-page/redux';
import { selectDestinationLocation, selectTakeOffLocation } from '@src/features/order/pages/upsert-order-page/redux';
import { CircularProgress } from '@mui/material';
import { ArcStack } from '@src/core/ui';
import { RouteWPS, TerrainAwarePanel } from '@src/features/order/pages/upsert-mission-page/components';
import DroneHomePath from '@src/features/order/pages/upsert-mission-page/components/drone-home-path';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { DroneStatusType, OrderEntity, SettingUnitType, useCheckAndPlanMutation, useDroneProvider, useGetSettingByUnitType } from '@src/core/features';
import { ArcDroneMarker } from '@src/core/controllers';
import { arcConvertBetweenUnitTypes } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType } from '@src/core/enums';
import { trackError, toTrackedError } from '@src/core/sentry';

const CreateRouteMap: React.FC<CreateRouteMapProps> = (props) => {
  const { hideRouteDestination = false } = props;
  const dispatch = useDispatch();
  const effectiveOrderId = useWizardOrderId();
  const { missionId } = useParams<MissionIdParams>();
  const isEditMission = Boolean(missionId);

  const selected = useSelector(selectDrone);
  const { data, isLoading } = useGetOrderQuery(effectiveOrderId ? effectiveOrderId : skipToken);
  
  const [checkAndPlan, { isLoading: isChecking }] = useCheckAndPlanMutation();
  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);

  // Show the loader the instant the user marks a destination (the redux value updates
  // synchronously on click), not several seconds later when checkAndPlan finally fires
  // after the debounced order-persist + reload. It clears once the route plan resolves.
  const destinationLocation = useSelector(selectDestinationLocation);
  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationKey = `${destinationLocation?.latitude ?? ''}-${destinationLocation?.longitude ?? ''}`;
  const takeoffKey = `${takeOffLocation?.latitude ?? ''}-${takeOffLocation?.longitude ?? ''}`;
  const [planning, setPlanning] = React.useState(false);
  const prevDestinationKeyRef = React.useRef(destinationKey);
  const prevTakeoffKeyRef = React.useRef(takeoffKey);
  const waypoints = useSelector(selectWaypoints);
  const routeModified = useSelector(selectRouteModified);
  const editableRoute = useSelector(selectEditableRoute);
  const routeLocked = editableRoute || routeModified;
  const lastPlanSigRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (destinationKey === prevDestinationKeyRef.current) return;
    prevDestinationKeyRef.current = destinationKey;
    if (routeLocked) return;
    const hasDestination = Boolean(destinationLocation?.latitude && destinationLocation?.longitude);
    // Showing the loader immediately also unmounts the route below, so the stale route line
    // disappears the moment a new destination is marked (until the new plan resolves).
    if (hasDestination) setPlanning(true);
  }, [destinationKey, destinationLocation, routeLocked]);


  React.useEffect(() => {
    if (takeoffKey === prevTakeoffKeyRef.current) return;
    prevTakeoffKeyRef.current = takeoffKey;
    if (routeLocked) return;
    const hasTakeoff = Boolean(takeOffLocation?.latitude && takeOffLocation?.longitude);
    const hasDestination = Boolean(destinationLocation?.latitude && destinationLocation?.longitude);
    if (hasTakeoff && hasDestination) setPlanning(true);
  }, [takeoffKey, takeOffLocation, destinationLocation, routeLocked]);

  React.useEffect(() => {
    if (!routeLocked || waypoints.length === 0) return;
    const tLat = Number(takeOffLocation?.latitude);
    const tLng = Number(takeOffLocation?.longitude);
    const dLat = Number(destinationLocation?.latitude);
    const dLng = Number(destinationLocation?.longitude);

    let changed = false;
    const next = waypoints.map((wp) => {
      if (wp.type === RouteWaypointType.TAKE_OFF && Number.isFinite(tLat) && Number.isFinite(tLng)) {
        if (Number(wp.latitude) !== tLat || Number(wp.longitude) !== tLng) {
          changed = true;
          return { ...wp, latitude: tLat, longitude: tLng };
        }
      }
      if (wp.type === RouteWaypointType.DESTINATION && Number.isFinite(dLat) && Number.isFinite(dLng)) {
        if (Number(wp.latitude) !== dLat || Number(wp.longitude) !== dLng) {
          changed = true;
          return { ...wp, latitude: dLat, longitude: dLng };
        }
      }
      return wp;
    });
    if (changed) dispatch(setWaypoints(next));
  }, [routeLocked, takeoffKey, destinationKey, waypoints, takeOffLocation, destinationLocation, dispatch]);

  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const unitType = useUnitType();
  // FLIGHT_ALTITUDE setting now arrives in SI (m). Waypoint altitudes are stored/edited
  // in the user's display unit (see EnterRouteForm) and submitted with unitSystem, so
  // convert SI -> display before seeding waypoints.
  const flightAltitudeDisplay = React.useMemo(
    () => arcConvertBetweenUnitTypes(Number(flightAltitude.value), MeasurementType.DISTANCE, UnitType.SI, getUnitType()),
    [flightAltitude.value, unitType],
  );

  const coalesceCoord = (primary?: string | number | null, fallback?: string | number | null) => {
    const parsed = Number(primary);
    if (primary != null && primary !== '' && Number.isFinite(parsed)) return parsed;
    return Number(fallback);
  };

  const takeoffLat = coalesceCoord(takeOffLocation?.latitude, data?.takeoffLocation?.latitude);
  const takeoffLng = coalesceCoord(takeOffLocation?.longitude, data?.takeoffLocation?.longitude);

  const destinationLat = Number(data?.destinationLocation?.latitude);
  const destinationLng = Number(data?.destinationLocation?.longitude);


  const onCheckAndPlan = (order: OrderEntity) => {
    const payload = [
      { latitude: takeoffLat, longitude: takeoffLng },
      { orderId: order.id, latitude: destinationLat, longitude: destinationLng },
      { latitude: takeoffLat, longitude: takeoffLng },
    ];


    const prevTakeoffAltitude = waypoints.find((w) => w.type === RouteWaypointType.TAKE_OFF)?.altitude;
    const prevDestinationAltitude = waypoints.find((w) => w.type === RouteWaypointType.DESTINATION)?.altitude;

    checkAndPlan({ path: payload }).unwrap().then((response) => {
      const wps = response.map((wp, idx) => {
        const updatedWp: RouteWaypointEntity = {
          uuid: uuidv4(),
          orderId: wp.orderId,
          latitude: wp.latitude,
          longitude: wp.longitude,
          type: RouteWaypointType.WAYPOINT,
          altitude: flightAltitudeDisplay,
        };
        if (wp.orderId) {
          return {
            ...updatedWp,
            type: RouteWaypointType.DESTINATION,
            altitude: prevDestinationAltitude ?? flightAltitudeDisplay,
          };
        }
        if (idx === 0 || idx === response.length - 1) {
          return {
            ...updatedWp,
            type: RouteWaypointType.TAKE_OFF,
            altitude: prevTakeoffAltitude ?? flightAltitudeDisplay,
          };
        }
        return updatedWp;
      });
      dispatch(setWaypoints(wps));
      setPlanning(false);
    }).catch((err: unknown) => {
      setPlanning(false);
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'checkAndPlan' } });
    });
  };


  const planSig = isEditMission
    ? `${destinationLat},${destinationLng}`
    : `${takeoffLat},${takeoffLng}|${destinationLat},${destinationLng}`;

  React.useEffect(() => {
    if (!data || !flightAltitude.value) return;
    if (!Number.isFinite(destinationLat) || !Number.isFinite(destinationLng)) return;

    // Never auto-plan while the user is editing / has modified the route — just keep the
    // baseline signature in sync so a later unlock doesn't trigger a stale replan.
    if (routeLocked) {
      lastPlanSigRef.current = planSig;
      setPlanning(false);
      return;
    }

    const isFirstPass = lastPlanSigRef.current === null;
    if (isFirstPass) {
      // Adopt an already-present route instead of planning a fresh straight one:
      //  - redux already holds waypoints (restored by the sidebar), or
      //  - a NEW order re-opened after Save & Exit carries its saved route on `data.waypoints`.
      const hasExistingRoute = waypoints.length > 0 || (!isEditMission && Boolean(data.waypoints?.length));
      if (hasExistingRoute) {
        lastPlanSigRef.current = planSig;
        setPlanning(false);
        return;
      }
      // Editing a mission with no route yet: wait for it to load (don't baseline the sig,
      // so the route is adopted once it arrives) rather than planning a straight one.
      if (isEditMission) {
        setPlanning(false);
        return;
      }
    }

    // Nothing changed since the last plan.
    if (lastPlanSigRef.current === planSig) {
      setPlanning(false);
      return;
    }

    lastPlanSigRef.current = planSig;
    onCheckAndPlan(data);
  }, [data, flightAltitude.value, planSig, waypoints.length, isEditMission, routeLocked]);

  // While the route is being (re)planned after a destination/drone change, cover the map
  // with a centered loader instead of flashing an empty map. `planning` reacts immediately
  // to the destination click; `isChecking`/`isLoading` cover the actual request.

  const showLoader = isEditMission ? isChecking : (planning || isLoading || isChecking);
  if (showLoader) {
    return (
      <ArcStack
        alignItems='center'
        justifyContent='center'
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
        }}>
        <CircularProgress size={72} sx={{ color: 'common.white' }} />
      </ArcStack>
    );
  }

  return (
    <React.Fragment>
      {idleDrones.map((drone) => {
        if (drone.id === selected?.id) {
          return (
            <ArcDroneMarker.idleSelected 
              key={drone.id} 
              position={{ 
                lat: Number(drone.latitude), 
                lng: Number(drone.longitude),
              }} 
            />
          );
        }
        return ( 
          <ArcDroneMarker.idleArrow 
            key={drone.id}
            position={{ 
              lat: Number(drone.latitude), 
              lng: Number(drone.longitude),
            }}  
          />
        );
      })}
      {selected && !idleDrones.some((d) => d.id === selected.id) && (
        <ArcDroneMarker.idleSelected
          position={{ lat: Number(selected.latitude), lng: Number(selected.longitude) }}
        />
      )}
      {selected && Number.isFinite(takeoffLat) && Number.isFinite(takeoffLng) && (
        <DroneHomePath
          from={{ lat: Number(selected.latitude), lng: Number(selected.longitude) }}
          to={{ lat: takeoffLat, lng: takeoffLng }}
        />
      )}
      <RouteWPS hideDestinationMarker={hideRouteDestination} takeoffDrone='hidden' />
      <TerrainAwarePanel />
    </React.Fragment>
  );
};

export { CreateRouteMap };
export default CreateRouteMap;